import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const Settings = () => {
  const [settings, setSettings] = useState({
    autoRefresh: true,
    refreshInterval: 30,
    notifications: true,
    exportFormat: 'xlsx',
    theme: 'dark',
    language: 'en',
    twoFactorEnabled: false,
    sessionTimeout: 60,
  });

  const [apiSettings, setApiSettings] = useState({
    apiBaseUrl: 'http://127.0.0.1:9000',
    timeout: 30,
    retryAttempts: 3,
  });

  useEffect(() => {
    const savedSettings = JSON.parse(localStorage.getItem('app_settings') || '{}');
    if (Object.keys(savedSettings).length > 0) {
      setSettings(savedSettings);
    }
    
    const savedApiSettings = JSON.parse(localStorage.getItem('api_settings') || '{}');
    if (Object.keys(savedApiSettings).length > 0) {
      setApiSettings(savedApiSettings);
    }
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleApiSettingChange = (key, value) => {
    setApiSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = () => {
    localStorage.setItem('app_settings', JSON.stringify(settings));
    localStorage.setItem('api_settings', JSON.stringify(apiSettings));
    
    toast.success('Settings saved successfully!');
  };

  const handleResetSettings = () => {
    const defaultSettings = {
      autoRefresh: true,
      refreshInterval: 30,
      notifications: true,
      exportFormat: 'xlsx',
      theme: 'dark',
      language: 'en',
      twoFactorEnabled: false,
      sessionTimeout: 60,
    };
    
    const defaultApiSettings = {
      apiBaseUrl: 'http://127.0.0.1:9000',
      timeout: 30,
      retryAttempts: 3,
    };
    
    setSettings(defaultSettings);
    setApiSettings(defaultApiSettings);
    localStorage.setItem('app_settings', JSON.stringify(defaultSettings));
    localStorage.setItem('api_settings', JSON.stringify(defaultApiSettings));
    toast.success('Settings reset to defaults!');
  };

  const handleTestApiConnection = async () => {
    try {
      const token = localStorage.getItem('telegram_token');
      if (!token) {
        toast.error('Please login first');
        return;
      }

      const response = await fetch(`${apiSettings.apiBaseUrl}/telegram/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('API connection successful!');
      } else {
        toast.error('API connection failed');
      }
    } catch (error) {
      toast.error('Connection error: ' + error.message);
    }
  };

  const exportFormats = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV (.csv)' },
    { value: 'json', label: 'JSON (.json)' },
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleResetSettings}
          >
            Reset Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
          >
            Save Settings
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, bgcolor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <SettingsIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  General Settings
                </Typography>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Enable Notifications" 
                    secondary="Show toast notifications for actions"
                  />
                  <Switch
                    checked={settings.notifications}
                    onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Auto Refresh" 
                    secondary="Automatically refresh data"
                  />
                  <Switch
                    checked={settings.autoRefresh}
                    onChange={(e) => handleSettingChange('autoRefresh', e.target.checked)}
                  />
                </ListItem>

                {settings.autoRefresh && (
                  <ListItem>
                    <TextField
                      fullWidth
                      type="number"
                      label="Refresh Interval (seconds)"
                      value={settings.refreshInterval}
                      onChange={(e) => handleSettingChange('refreshInterval', parseInt(e.target.value))}
                      inputProps={{ min: 10, max: 300 }}
                    />
                  </ListItem>
                )}

                <ListItem>
                  <ListItemIcon>
                    <LanguageIcon />
                  </ListItemIcon>
                  <TextField
                    fullWidth
                    select
                    label="Language"
                    value={settings.language}
                    onChange={(e) => handleSettingChange('language', e.target.value)}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </TextField>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <DarkModeIcon />
                  </ListItemIcon>
                  <TextField
                    fullWidth
                    select
                    label="Theme"
                    value={settings.theme}
                    onChange={(e) => handleSettingChange('theme', e.target.value)}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                    <option value="auto">Auto</option>
                  </TextField>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Session Timeout (minutes)" 
                    secondary="Auto logout after inactivity"
                  />
                  <TextField
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                    inputProps={{ min: 5, max: 240 }}
                    sx={{ width: 100 }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, bgcolor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <SecurityIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Export & API Settings
                </Typography>
              </Box>

              <List>
                <ListItem>
                  <TextField
                    fullWidth
                    select
                    label="Default Export Format"
                    value={settings.exportFormat}
                    onChange={(e) => handleSettingChange('exportFormat', e.target.value)}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    {exportFormats.map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </TextField>
                </ListItem>

                <Divider sx={{ my: 2 }} />

                <ListItem>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    API Configuration
                  </Typography>
                </ListItem>

                <ListItem>
                  <TextField
                    fullWidth
                    label="API Base URL"
                    value={apiSettings.apiBaseUrl}
                    onChange={(e) => handleApiSettingChange('apiBaseUrl', e.target.value)}
                    placeholder="http://127.0.0.1:9000"
                  />
                </ListItem>

                <ListItem>
                  <TextField
                    fullWidth
                    type="number"
                    label="Request Timeout (seconds)"
                    value={apiSettings.timeout}
                    onChange={(e) => handleApiSettingChange('timeout', parseInt(e.target.value))}
                    inputProps={{ min: 5, max: 120 }}
                  />
                </ListItem>

                <ListItem>
                  <TextField
                    fullWidth
                    type="number"
                    label="Retry Attempts"
                    value={apiSettings.retryAttempts}
                    onChange={(e) => handleApiSettingChange('retryAttempts', parseInt(e.target.value))}
                    inputProps={{ min: 0, max: 10 }}
                  />
                </ListItem>

                <ListItem>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleTestApiConnection}
                  >
                    Test API Connection
                  </Button>
                </ListItem>
              </List>

              <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> API changes require page refresh to take effect.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;